import React, { useState, useMemo } from "react";
import {
  Share2,
  Send,
  Sparkles,
  RefreshCw,
  ExternalLink,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  Globe,
  Image as ImageIcon,
  Link as LinkIcon,
  Trash2,
  Sliders,
  Check,
  Search,
  Filter,
  Layers,
  Info,
  Key,
  Shield,
  Eye,
  X,
  FileText,
  Copy,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Button from "../../components/common/Button";
import { Input } from "../../components/common/Input";
import Modal from "../../components/common/Modal";
import { MediaPickerModal } from "../../components/common/MediaPickerModal";
import { useToast } from "../../hooks/otherHooks/useToast";
import { MediaAsset } from "../../services/mediaService";
import {
  useSocialBroadcastsQuery,
  useSendBroadcastMutation,
  useDeleteBroadcastMutation,
  useSocialConfigsQuery,
  useCreateSocialConfigMutation,
  useUpdateSocialConfigMutation,
  useSocialLogsQuery,
} from "../../hooks/queries/useSocialBroadcastQueries";
import {
  SocialBroadcast,
  SocialPostLog,
  SocialPlatform,
  SocialPlatformConfig,
  SocialPlatformConfigPayload,
} from "../../services/socialBroadcastService";

// Platform styling definitions
const PLATFORMS_META: Record<
  SocialPlatform,
  {
    name: string;
    brandColor: string;
    bgBadge: string;
    textBadge: string;
    borderBadge: string;
    iconSvg: React.ReactNode;
    charLimit: number;
    helpText: string;
  }
> = {
  facebook: {
    name: "Facebook Page",
    brandColor: "#1877F2",
    bgBadge: "cht-bg-blue-50",
    textBadge: "cht-text-blue-700",
    borderBadge: "cht-border-blue-200",
    iconSvg: (
      <svg className="cht-w-4 cht-h-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
    charLimit: 63206,
    helpText: "Posts directly to the configured organization Facebook Page feed.",
  },
  instagram: {
    name: "Instagram Business",
    brandColor: "#E1306C",
    bgBadge: "cht-bg-pink-50",
    textBadge: "cht-text-pink-700",
    borderBadge: "cht-border-pink-200",
    iconSvg: (
      <svg className="cht-w-4 cht-h-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
      </svg>
    ),
    charLimit: 2200,
    helpText: "Requires an image attachment URL to publish on Instagram feed.",
  },
  twitter: {
    name: "X (Twitter)",
    brandColor: "#000000",
    bgBadge: "cht-bg-gray-100",
    textBadge: "cht-text-gray-900",
    borderBadge: "cht-border-gray-300",
    iconSvg: (
      <svg className="cht-w-4 cht-h-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
    charLimit: 280,
    helpText: "Tweets capped at 280 characters. Attaches image as Twitter media.",
  },
  linkedin: {
    name: "LinkedIn",
    brandColor: "#0A66C2",
    bgBadge: "cht-bg-sky-50",
    textBadge: "cht-text-sky-700",
    borderBadge: "cht-border-sky-200",
    iconSvg: (
      <svg className="cht-w-4 cht-h-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
      </svg>
    ),
    charLimit: 3000,
    helpText: "Publishes article or update on LinkedIn organization page or profile.",
  },
  threads: {
    name: "Threads",
    brandColor: "#000000",
    bgBadge: "cht-bg-neutral-100",
    textBadge: "cht-text-neutral-900",
    borderBadge: "cht-border-neutral-300",
    iconSvg: (
      <svg className="cht-w-4 cht-h-4" fill="currentColor" viewBox="0 0 192 192">
        <path d="M141.537 88.9883C140.71 88.5919 139.87 88.2104 139.019 87.8451C137.537 60.5382 122.616 44.905 97.5619 44.745C97.4484 44.7443 97.3355 44.7443 97.222 44.745C75.0505 44.745 57.5134 59.9575 56.4678 88.5447C56.3685 91.2623 58.5085 93.5354 61.2261 93.6348C63.9437 93.7341 66.2168 91.5941 66.3161 88.8765C67.199 64.733 80.8924 54.4074 97.2215 54.4074C97.3105 54.4074 97.3995 54.4074 97.4885 54.4081C117.202 54.534 128.271 67.4398 129.351 90.165C117.653 92.4277 106.111 96.6712 95.845 103.047C76.9935 114.757 70.0886 128.528 77.0142 142.102C82.4939 152.846 93.9961 157.994 107.545 155.938C123.632 153.497 134.721 142.275 139.878 123.181C148.971 129.412 155.158 137.893 155.858 147.933C156.974 163.957 144.902 176.621 126.155 178.691C107.962 180.7 91.6853 173.342 81.334 158.468C79.7438 156.183 76.6125 155.603 74.3275 157.193C72.0425 158.783 71.4625 161.914 73.0525 164.199C85.5898 182.213 105.155 190.96 127.214 188.525C150.812 185.918 166.426 169.601 165.006 149.239C164.048 135.502 155.334 124.084 141.537 116.142V88.9883ZM130.419 114.475C125.759 130.129 117.658 145.414 106.115 147.165C97.1009 148.532 88.5085 144.331 85.3403 138.122C81.189 129.988 86.8524 119.824 101.077 111.002C110.158 105.367 120.301 101.488 130.419 99.3094V114.475Z" />
      </svg>
    ),
    charLimit: 500,
    helpText: "Publishes text posts and images directly to Meta Threads feed (up to 500 characters).",
  },
  tiktok: {
    name: "TikTok",
    brandColor: "#000000",
    bgBadge: "cht-bg-neutral-900/10",
    textBadge: "cht-text-neutral-900",
    borderBadge: "cht-border-neutral-300",
    iconSvg: (
      <svg className="cht-w-4 cht-h-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.298-.002.595.042.88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.04-.1z" />
      </svg>
    ),
    charLimit: 2200,
    helpText: "Publishes direct photo/video posts to TikTok account. Media attachment URL required.",
  },
};

const ALL_PLATFORMS: SocialPlatform[] = [
  "facebook",
  "instagram",
  "twitter",
  "linkedin",
  "threads",
  "tiktok",
];

export interface SocialBroadcastProps {
  isSuperAdmin?: boolean;
}

const SocialBroadcastView: React.FC<SocialBroadcastProps> = ({ isSuperAdmin = true }) => {
  const { addToast } = useToast();

  // Active Tab
  const [activeTab, setActiveTab] = useState<"composer" | "history" | "logs" | "credentials">("composer");

  // Broadcast Composer State
  const [title, setTitle] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [linkUrl, setLinkUrl] = useState<string>("");
  const [imageUrl, setImageUrl] = useState<string>("");
  const [selectedPlatforms, setSelectedPlatforms] = useState<SocialPlatform[]>(ALL_PLATFORMS);
  const [previewPlatform, setPreviewPlatform] = useState<SocialPlatform>("facebook");

  // Modals state
  const [isMediaPickerOpen, setIsMediaPickerOpen] = useState<boolean>(false);
  const [inspectBroadcast, setInspectBroadcast] = useState<SocialBroadcast | null>(null);
  const [deletingBroadcastId, setDeletingBroadcastId] = useState<number | null>(null);

  // Platform Config Edit State
  const [editingConfig, setEditingConfig] = useState<SocialPlatformConfig | null>(null);
  const [configForm, setConfigForm] = useState<SocialPlatformConfigPayload>({
    platform: "facebook",
    is_enabled: true,
    page_or_account_id: "",
    access_token: "",
    api_key: "",
    api_secret: "",
    bearer_token: "",
    access_token_secret: "",
  });

  // History & Logs Filters
  const [historySearch, setHistorySearch] = useState<string>("");
  const [logPlatformFilter, setLogPlatformFilter] = useState<string>("all");
  const [logStatusFilter, setLogStatusFilter] = useState<string>("all");

  // TanStack Queries & Mutations
  const {
    data: broadcasts = [],
    isLoading: broadcastsLoading,
    refetch: refetchBroadcasts,
    isFetching: isFetchingBroadcasts,
  } = useSocialBroadcastsQuery();

  const {
    data: configs = [],
    isLoading: configsLoading,
    refetch: refetchConfigs,
  } = useSocialConfigsQuery();

  const {
    data: logs = [],
    isLoading: logsLoading,
    refetch: refetchLogs,
  } = useSocialLogsQuery({
    platform: logPlatformFilter,
    status: logStatusFilter,
  });

  const sendBroadcastMutation = useSendBroadcastMutation();
  const deleteBroadcastMutation = useDeleteBroadcastMutation();
  const createConfigMutation = useCreateSocialConfigMutation();
  const updateConfigMutation = useUpdateSocialConfigMutation();

  // Pre-fill quick templates
  const applyTemplate = (type: "course" | "blog" | "admissions" | "tech") => {
    switch (type) {
      case "course":
        setTitle("New Intensive Training Program Launch");
        setMessage(
          "🚀 Supercharge your tech career! Enroll now in our cutting-edge industry training course. Master modern architectures, practical projects, and gain hands-on expertise with direct mentorship.\n\n#TechSkills #CodehubTechnologies #LearnCoding"
        );
        setLinkUrl("https://codehubtech.org/courses");
        break;
      case "blog":
        setTitle("Featured Tech Insight & Engineering Guide");
        setMessage(
          "💡 Read our latest architectural deep-dive into full-stack modern web engineering. Discover best practices, real-world case studies, and scalable design patterns.\n\nRead the full article below 👇\n#WebDevelopment #SoftwareEngineering"
        );
        setLinkUrl("https://codehubtech.org/blog");
        break;
      case "admissions":
        setTitle("Admissions Open: Industrial Training & ICT Career Tracks");
        setMessage(
          "🎓 Take the leap from theory to high-impact software engineering! Applications are now open for our upcoming cohort. Build portfolio-ready production projects under senior engineers.\n\nApply today at Codehub Technologies.\n#CareerGrowth #TechEducation"
        );
        setLinkUrl("https://codehubtech.org/admission");
        break;
      case "tech":
        setTitle("Engineering Innovation Update");
        setMessage(
          "⚡ We are proud to announce major platform upgrades, automated CI pipelines, and cloud-native integration at Codehub Technologies LTD. Empowering students and corporate clients with world-class engineering solutions."
        );
        setLinkUrl("https://codehubtech.org");
        break;
    }
    addToast({
      message: "Template content loaded into composer.",
      bg: "info",
    });
  };

  // Toggle platform selection
  const togglePlatform = (p: SocialPlatform) => {
    if (selectedPlatforms.includes(p)) {
      if (selectedPlatforms.length === 1) {
        addToast({
          message: "Select at least one social media platform to broadcast.",
          bg: "warning",
        });
        return;
      }
      setSelectedPlatforms(selectedPlatforms.filter((item) => item !== p));
    } else {
      setSelectedPlatforms([...selectedPlatforms, p]);
    }
  };

  // Select all or reset
  const selectAllPlatforms = () => setSelectedPlatforms([...ALL_PLATFORMS]);

  // Handle Dispatch Broadcast
  const handleBroadcastSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) {
      addToast({
        message: "Please write a message or caption to broadcast.",
        bg: "danger",
      });
      return;
    }

    if (selectedPlatforms.length === 0) {
      addToast({
        message: "Please select at least one social media platform.",
        bg: "warning",
      });
      return;
    }

    // Instagram check: Requires image
    if (selectedPlatforms.includes("instagram") && !imageUrl.trim()) {
      addToast({
        message: "Instagram requires an image attachment. Please provide an image URL or choose from Media Library.",
        bg: "warning",
      });
      return;
    }

    // TikTok check: Requires image / media
    if (selectedPlatforms.includes("tiktok") && !imageUrl.trim()) {
      addToast({
        message: "TikTok requires a media attachment (image or video URL). Please provide an image URL or choose from Media Library.",
        bg: "warning",
      });
      return;
    }

    sendBroadcastMutation.mutate(
      {
        title: title.trim() || undefined,
        message: message.trim(),
        link_url: linkUrl.trim() || undefined,
        image_url: imageUrl.trim() || undefined,
        platforms: selectedPlatforms,
      },
      {
        onSuccess: (data) => {
          addToast({
            message: `Broadcast #${data.id} dispatched successfully across ${data.target_platforms.length} platform(s)!`,
            bg: "success",
          });
          // Reset form
          setTitle("");
          setMessage("");
          setLinkUrl("");
          setImageUrl("");
          setActiveTab("history");
        },
        onError: (err: any) => {
          addToast({
            message: err.response?.data?.detail || err.message || "Failed to dispatch social broadcast.",
            bg: "danger",
          });
        },
      }
    );
  };

  // Handle Delete Confirmation
  const handleDeleteConfirm = () => {
    if (!deletingBroadcastId) return;
    deleteBroadcastMutation.mutate(deletingBroadcastId, {
      onSuccess: () => {
        addToast({
          message: "Social broadcast record deleted successfully.",
          bg: "success",
        });
        setDeletingBroadcastId(null);
        if (inspectBroadcast?.id === deletingBroadcastId) {
          setInspectBroadcast(null);
        }
      },
      onError: (err: any) => {
        addToast({
          message: err.message || "Failed to delete broadcast record.",
          bg: "danger",
        });
      },
    });
  };

  // Handle Platform Config Edit Modal Open
  const openConfigModal = (platform: SocialPlatform) => {
    const existing = configs.find((c) => c.platform === platform);
    if (existing) {
      setEditingConfig(existing);
      setConfigForm({
        platform: existing.platform,
        is_enabled: existing.is_enabled,
        page_or_account_id: existing.page_or_account_id || "",
        access_token: "",
        api_key: "",
        api_secret: "",
        bearer_token: "",
        access_token_secret: "",
      });
    } else {
      setEditingConfig(null);
      setConfigForm({
        platform,
        is_enabled: true,
        page_or_account_id: "",
        access_token: "",
        api_key: "",
        api_secret: "",
        bearer_token: "",
        access_token_secret: "",
      });
    }
  };

  // Save Platform Config Override
  const handleSaveConfig = (e: React.FormEvent) => {
    e.preventDefault();
    const payload: Partial<SocialPlatformConfigPayload> = {
      platform: configForm.platform,
      is_enabled: configForm.is_enabled,
      page_or_account_id: configForm.page_or_account_id || undefined,
    };
    if (configForm.access_token) payload.access_token = configForm.access_token;
    if (configForm.api_key) payload.api_key = configForm.api_key;
    if (configForm.api_secret) payload.api_secret = configForm.api_secret;
    if (configForm.bearer_token) payload.bearer_token = configForm.bearer_token;
    if (configForm.access_token_secret) payload.access_token_secret = configForm.access_token_secret;

    if (editingConfig) {
      updateConfigMutation.mutate(
        { id: editingConfig.id, payload },
        {
          onSuccess: () => {
            addToast({
              message: `${PLATFORMS_META[configForm.platform as SocialPlatform]?.name} settings updated.`,
              bg: "success",
            });
            setEditingConfig(null);
            refetchConfigs();
          },
          onError: (err: any) => {
            addToast({
              message: err.message || "Failed to update platform settings.",
              bg: "danger",
            });
          },
        }
      );
    } else {
      createConfigMutation.mutate(payload as SocialPlatformConfigPayload, {
        onSuccess: () => {
          addToast({
            message: `${PLATFORMS_META[configForm.platform as SocialPlatform]?.name} configuration created.`,
            bg: "success",
          });
          setEditingConfig(null);
          refetchConfigs();
        },
        onError: (err: any) => {
          addToast({
            message: err.message || "Failed to create configuration override.",
            bg: "danger",
          });
        },
      });
    }
  };

  // Filtered broadcast campaigns
  const filteredBroadcasts = useMemo(() => {
    return broadcasts.filter((item) => {
      const q = historySearch.toLowerCase().trim();
      if (!q) return true;
      return (
        item.message.toLowerCase().includes(q) ||
        (item.title && item.title.toLowerCase().includes(q)) ||
        (item.link_url && item.link_url.toLowerCase().includes(q))
      );
    });
  }, [broadcasts, historySearch]);

  // Overall statistics
  const stats = useMemo(() => {
    let totalPosts = 0;
    let publishedCount = 0;
    let failedCount = 0;
    let pendingCount = 0;

    broadcasts.forEach((b) => {
      b.logs?.forEach((l) => {
        totalPosts++;
        if (l.status === "published") publishedCount++;
        else if (l.status === "failed") failedCount++;
        else if (l.status === "pending") pendingCount++;
      });
    });

    return {
      totalCampaigns: broadcasts.length,
      totalPosts,
      publishedCount,
      failedCount,
      pendingCount,
    };
  }, [broadcasts]);

  // Status Badge Helper
  const renderStatusBadge = (status: string) => {
    switch (status) {
      case "published":
        return (
          <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2 cht-py-0.5 cht-rounded-full cht-text-[11px] cht-font-semibold cht-bg-emerald-50 cht-text-emerald-700 cht-border cht-border-emerald-200">
            <CheckCircle2 size={12} className="cht-text-emerald-600" />
            Published
          </span>
        );
      case "failed":
        return (
          <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2 cht-py-0.5 cht-rounded-full cht-text-[11px] cht-font-semibold cht-bg-rose-50 cht-text-rose-700 cht-border cht-border-rose-200">
            <XCircle size={12} className="cht-text-rose-600" />
            Failed
          </span>
        );
      case "pending":
        return (
          <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2 cht-py-0.5 cht-rounded-full cht-text-[11px] cht-font-semibold cht-bg-amber-50 cht-text-amber-700 cht-border cht-border-amber-200 cht-animate-pulse">
            <Clock size={12} className="cht-text-amber-600" />
            Pending
          </span>
        );
      case "skipped":
        return (
          <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2 cht-py-0.5 cht-rounded-full cht-text-[11px] cht-font-semibold cht-bg-gray-100 cht-text-gray-600 cht-border cht-border-gray-200">
            <Info size={12} />
            Skipped
          </span>
        );
      default:
        return (
          <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2 cht-py-0.5 cht-rounded-full cht-text-[11px] cht-font-semibold cht-bg-gray-100 cht-text-gray-700">
            {status}
          </span>
        );
    }
  };

  return (
    <div className="cht-space-y-6 cht-max-w-7xl cht-mx-auto cht-pb-16 cht-animate-in cht-fade-in cht-duration-500">
      {/* Hero Banner */}
      <div className="cht-rounded-[28px] cht-bg-gradient-to-r cht-from-[#1e293b] cht-via-indigo-950 cht-to-[#1e293b] cht-p-6 sm:cht-p-8 cht-text-white cht-relative cht-overflow-hidden cht-shadow-xl cht-border cht-border-indigo-900/40">
        <div className="cht-absolute cht-right-0 cht-top-0 cht-w-96 cht-h-96 cht-bg-[#3e57da]/15 cht-rounded-full cht-blur-3xl cht-pointer-events-none" />
        <div className="cht-relative cht-z-10 cht-flex cht-flex-col lg:cht-flex-row lg:cht-items-center lg:cht-justify-between cht-gap-6">
          <div className="cht-space-y-2">
            <div className="cht-inline-flex cht-items-center cht-gap-2 cht-px-3 cht-py-1 cht-rounded-full cht-bg-white/10 cht-border cht-border-white/15 cht-text-xs cht-font-medium cht-text-indigo-200">
              <Share2 size={13} className="cht-text-indigo-300" />
              <span>Multi-Platform Social Broadcast Engine</span>
            </div>
            <h1 className="cht-text-2xl sm:cht-text-3xl cht-font-black cht-tracking-tight">
              Social Media Command Center
            </h1>
            <p className="cht-text-sm cht-text-slate-300 cht-max-w-2xl cht-leading-relaxed">
              Compose once and broadcast simultaneously across Facebook Pages, Instagram Business,
              X (Twitter), LinkedIn, Threads, and TikTok with live status auditing and asynchronous worker dispatches.
            </p>
          </div>

          {/* Quick Metrics */}
          <div className="cht-grid cht-grid-cols-3 cht-gap-3 sm:cht-gap-4 cht-bg-white/5 cht-p-4 cht-rounded-2xl cht-border cht-border-white/10">
            <div className="cht-text-center">
              <div className="cht-text-xl sm:cht-text-2xl cht-font-extrabold cht-text-white">
                {stats.totalCampaigns}
              </div>
              <div className="cht-text-[11px] cht-text-slate-400 cht-font-medium">Campaigns</div>
            </div>
            <div className="cht-text-center cht-border-x cht-border-white/10 cht-px-2">
              <div className="cht-text-xl sm:cht-text-2xl cht-font-extrabold cht-text-emerald-400">
                {stats.publishedCount}
              </div>
              <div className="cht-text-[11px] cht-text-slate-400 cht-font-medium">Published</div>
            </div>
            <div className="cht-text-center">
              <div className="cht-text-xl sm:cht-text-2xl cht-font-extrabold cht-text-indigo-300">
                {ALL_PLATFORMS.length}
              </div>
              <div className="cht-text-[11px] cht-text-slate-400 cht-font-medium">Platforms</div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs Bar */}
      <div className="cht-flex cht-items-center cht-justify-between cht-gap-4 cht-border-b cht-border-gray-200 cht-pb-2">
        <div className="cht-flex cht-items-center cht-gap-2 cht-overflow-x-auto cht-no-scrollbar">
          <button
            onClick={() => setActiveTab("composer")}
            className={`cht-flex cht-items-center cht-gap-2 cht-px-4 cht-py-2.5 cht-rounded-xl cht-text-sm cht-font-bold cht-transition-all ${
              activeTab === "composer"
                ? "cht-bg-[#3e57da] cht-text-white cht-shadow-md"
                : "cht-text-gray-600 hover:cht-bg-gray-100"
            }`}
          >
            <Send size={15} />
            <span>Broadcast Composer</span>
          </button>
          <button
            onClick={() => setActiveTab("history")}
            className={`cht-flex cht-items-center cht-gap-2 cht-px-4 cht-py-2.5 cht-rounded-xl cht-text-sm cht-font-bold cht-transition-all ${
              activeTab === "history"
                ? "cht-bg-[#3e57da] cht-text-white cht-shadow-md"
                : "cht-text-gray-600 hover:cht-bg-gray-100"
            }`}
          >
            <Clock size={15} />
            <span>Campaign History</span>
            {broadcasts.length > 0 && (
              <span
                className={`cht-px-2 cht-py-0.5 cht-rounded-full cht-text-xs ${
                  activeTab === "history"
                    ? "cht-bg-white/20 cht-text-white"
                    : "cht-bg-gray-200 cht-text-gray-700"
                }`}
              >
                {broadcasts.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("logs")}
            className={`cht-flex cht-items-center cht-gap-2 cht-px-4 cht-py-2.5 cht-rounded-xl cht-text-sm cht-font-bold cht-transition-all ${
              activeTab === "logs"
                ? "cht-bg-[#3e57da] cht-text-white cht-shadow-md"
                : "cht-text-gray-600 hover:cht-bg-gray-100"
            }`}
          >
            <FileText size={15} />
            <span>Execution Audit Logs</span>
          </button>
          {isSuperAdmin && (
            <button
              onClick={() => setActiveTab("credentials")}
              className={`cht-flex cht-items-center cht-gap-2 cht-px-4 cht-py-2.5 cht-rounded-xl cht-text-sm cht-font-bold cht-transition-all ${
                activeTab === "credentials"
                  ? "cht-bg-[#3e57da] cht-text-white cht-shadow-md"
                  : "cht-text-gray-600 hover:cht-bg-gray-100"
              }`}
            >
              <Key size={15} />
              <span>Platform Settings</span>
            </button>
          )}
        </div>

        <div className="cht-flex cht-items-center cht-gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              refetchBroadcasts();
              refetchLogs();
              refetchConfigs();
              addToast({ message: "Social autoposter state refreshed.", bg: "info" });
            }}
            loading={isFetchingBroadcasts}
            leftIcon={<RefreshCw size={14} className={isFetchingBroadcasts ? "cht-animate-spin" : ""} />}
          >
            Sync
          </Button>
        </div>
      </div>

      {/* TAB 1: BROADCAST COMPOSER */}
      {activeTab === "composer" && (
        <div className="cht-grid cht-grid-cols-1 lg:cht-grid-cols-12 cht-gap-6 cht-items-start">
          {/* Main Composer Form */}
          <div className="lg:cht-col-span-7 cht-space-y-6">
            <div className="cht-bg-white cht-rounded-2xl cht-border cht-border-gray-200/80 cht-shadow-sm cht-p-6">
              <div className="cht-flex cht-items-center cht-justify-between cht-mb-4">
                <h2 className="cht-text-lg cht-font-bold cht-text-[#1e293b] cht-flex cht-items-center cht-gap-2">
                  <Sparkles size={18} className="cht-text-[#3e57da]" />
                  <span>Create New Social Broadcast</span>
                </h2>
                <div className="cht-text-xs cht-text-gray-400">Step 1 of 2: Content & Target</div>
              </div>

              {/* Quick Template Fill Buttons */}
              <div className="cht-mb-5 cht-p-3 cht-rounded-xl cht-bg-slate-50 cht-border cht-border-slate-200/60">
                <div className="cht-text-xs cht-font-semibold cht-text-slate-600 cht-mb-2 cht-flex cht-items-center cht-gap-1.5">
                  <Sparkles size={13} className="cht-text-amber-500" />
                  <span>Quick Templates:</span>
                </div>
                <div className="cht-flex cht-flex-wrap cht-gap-2">
                  <button
                    type="button"
                    onClick={() => applyTemplate("course")}
                    className="cht-text-xs cht-px-2.5 cht-py-1 cht-rounded-lg cht-bg-white cht-border cht-border-gray-200 cht-text-gray-700 hover:cht-border-[#3e57da] hover:cht-text-[#3e57da] cht-transition-colors"
                  >
                    🚀 Course Launch
                  </button>
                  <button
                    type="button"
                    onClick={() => applyTemplate("blog")}
                    className="cht-text-xs cht-px-2.5 cht-py-1 cht-rounded-lg cht-bg-white cht-border cht-border-gray-200 cht-text-gray-700 hover:cht-border-[#3e57da] hover:cht-text-[#3e57da] cht-transition-colors"
                  >
                    💡 Blog Spotlight
                  </button>
                  <button
                    type="button"
                    onClick={() => applyTemplate("admissions")}
                    className="cht-text-xs cht-px-2.5 cht-py-1 cht-rounded-lg cht-bg-white cht-border cht-border-gray-200 cht-text-gray-700 hover:cht-border-[#3e57da] hover:cht-text-[#3e57da] cht-transition-colors"
                  >
                    🎓 Career Admissions
                  </button>
                  <button
                    type="button"
                    onClick={() => applyTemplate("tech")}
                    className="cht-text-xs cht-px-2.5 cht-py-1 cht-rounded-lg cht-bg-white cht-border cht-border-gray-200 cht-text-gray-700 hover:cht-border-[#3e57da] hover:cht-text-[#3e57da] cht-transition-colors"
                  >
                    ⚡ Platform Update
                  </button>
                </div>
              </div>

              <form onSubmit={handleBroadcastSubmit} className="cht-space-y-4">
                {/* Title (Optional) */}
                <div>
                  <label className="cht-block cht-text-xs cht-font-bold cht-text-gray-700 cht-mb-1.5">
                    Campaign Title / Headline <span className="cht-text-gray-400 cht-font-normal">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Codehub Summer Internship 2026 Kickoff"
                    className="cht-w-full cht-px-3.5 cht-py-2.5 cht-text-sm cht-bg-gray-50/50 cht-border cht-border-gray-200 cht-rounded-xl focus:cht-bg-white focus:cht-border-[#3e57da] focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 cht-outline-none cht-transition-all"
                  />
                </div>

                {/* Message / Caption */}
                <div>
                  <div className="cht-flex cht-items-center cht-justify-between cht-mb-1.5">
                    <label className="cht-block cht-text-xs cht-font-bold cht-text-gray-700">
                      Message / Caption <span className="cht-text-rose-500">*</span>
                    </label>
                    {/* Multi-platform character count warning */}
                    <div
                      className={`cht-text-xs cht-font-semibold ${
                        (message.length > 280 && selectedPlatforms.includes("twitter")) ||
                        (message.length > 500 && selectedPlatforms.includes("threads"))
                          ? "cht-text-amber-600"
                          : "cht-text-gray-400"
                      }`}
                    >
                      {message.length} chars
                      {selectedPlatforms.includes("twitter") && message.length > 280
                        ? " (X max 280 exceeded)"
                        : selectedPlatforms.includes("threads") && message.length > 500
                        ? " (Threads max 500 exceeded)"
                        : selectedPlatforms.includes("twitter")
                        ? " (X max 280)"
                        : selectedPlatforms.includes("threads")
                        ? " (Threads max 500)"
                        : ""}
                    </div>
                  </div>
                  <textarea
                    rows={5}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Write an engaging message, caption, announcement, or release notes with hashtags..."
                    className="cht-w-full cht-px-3.5 cht-py-2.5 cht-text-sm cht-bg-gray-50/50 cht-border cht-border-gray-200 cht-rounded-xl focus:cht-bg-white focus:cht-border-[#3e57da] focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 cht-outline-none cht-transition-all"
                    required
                  />
                </div>

                {/* Destination Link URL */}
                <div>
                  <label className="cht-block cht-text-xs cht-font-bold cht-text-gray-700 cht-mb-1.5">
                    Target Destination URL <span className="cht-text-gray-400 cht-font-normal">(Article, course, or promo link)</span>
                  </label>
                  <div className="cht-relative">
                    <LinkIcon
                      size={15}
                      className="cht-absolute cht-left-3.5 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400"
                    />
                    <input
                      type="url"
                      value={linkUrl}
                      onChange={(e) => setLinkUrl(e.target.value)}
                      placeholder="https://codehubtech.org/courses/full-stack-web-development"
                      className="cht-w-full cht-pl-10 cht-pr-3.5 cht-py-2.5 cht-text-sm cht-bg-gray-50/50 cht-border cht-border-gray-200 cht-rounded-xl focus:cht-bg-white focus:cht-border-[#3e57da] focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 cht-outline-none cht-transition-all"
                    />
                  </div>
                </div>

                {/* Image Attachment URL & Media Library Picker */}
                <div>
                  <label className="cht-block cht-text-xs cht-font-bold cht-text-gray-700 cht-mb-1.5">
                    Image Attachment <span className="cht-text-gray-400 cht-font-normal">(Cloudinary / Public HTTPS URL)</span>
                  </label>
                  <div className="cht-flex cht-gap-2">
                    <div className="cht-relative cht-flex-1">
                      <ImageIcon
                        size={15}
                        className="cht-absolute cht-left-3.5 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400"
                      />
                      <input
                        type="url"
                        value={imageUrl}
                        onChange={(e) => setImageUrl(e.target.value)}
                        placeholder="https://res.cloudinary.com/codehub/image/upload/banner.jpg"
                        className="cht-w-full cht-pl-10 cht-pr-3.5 cht-py-2.5 cht-text-sm cht-bg-gray-50/50 cht-border cht-border-gray-200 cht-rounded-xl focus:cht-bg-white focus:cht-border-[#3e57da] focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 cht-outline-none cht-transition-all"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsMediaPickerOpen(true)}
                      className="cht-shrink-0"
                    >
                      Media Library
                    </Button>
                  </div>

                  {/* Image Preview if provided */}
                  {imageUrl && (
                    <div className="cht-mt-2.5 cht-relative cht-rounded-xl cht-overflow-hidden cht-border cht-border-gray-200 cht-bg-gray-50 cht-p-2 cht-flex cht-items-center cht-gap-3">
                      <img
                        src={imageUrl}
                        alt="Attachment preview"
                        className="cht-w-16 cht-h-16 cht-object-cover cht-rounded-lg cht-border cht-border-gray-200"
                        onError={(e) => {
                          (e.target as HTMLElement).style.display = "none";
                        }}
                      />
                      <div className="cht-flex-1 cht-min-w-0">
                        <div className="cht-text-xs cht-font-bold cht-text-gray-800 cht-truncate">
                          Attached Image URL
                        </div>
                        <div className="cht-text-[11px] cht-text-gray-500 cht-truncate">
                          {imageUrl}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setImageUrl("")}
                        className="cht-p-1.5 cht-text-gray-400 hover:cht-text-rose-600 cht-transition-colors"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  )}
                </div>

                {/* Target Platforms Selector */}
                <div className="cht-pt-2">
                  <div className="cht-flex cht-items-center cht-justify-between cht-mb-2">
                    <label className="cht-block cht-text-xs cht-font-bold cht-text-gray-700">
                      Target Social Platforms <span className="cht-text-rose-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={selectAllPlatforms}
                      className="cht-text-xs cht-text-[#3e57da] hover:cht-underline cht-font-semibold"
                    >
                      Select All
                    </button>
                  </div>

                  <div className="cht-grid cht-grid-cols-2 sm:cht-grid-cols-3 lg:cht-grid-cols-6 cht-gap-2.5">
                    {ALL_PLATFORMS.map((platform) => {
                      const meta = PLATFORMS_META[platform];
                      const isSelected = selectedPlatforms.includes(platform);
                      return (
                        <button
                          key={platform}
                          type="button"
                          onClick={() => togglePlatform(platform)}
                          className={`cht-flex cht-items-center cht-gap-2.5 cht-p-3 cht-rounded-xl cht-border cht-text-left cht-transition-all ${
                            isSelected
                              ? "cht-border-[#3e57da] cht-bg-[#3e57da]/5 cht-shadow-sm"
                              : "cht-border-gray-200 cht-bg-white cht-opacity-60 hover:cht-opacity-100"
                          }`}
                        >
                          <div
                            className={`cht-w-7 cht-h-7 cht-rounded-lg cht-flex cht-items-center cht-justify-center cht-text-white`}
                            style={{ backgroundColor: meta.brandColor }}
                          >
                            {meta.iconSvg}
                          </div>
                          <div className="cht-flex-1 cht-min-w-0">
                            <div className="cht-text-xs cht-font-bold cht-text-gray-900 cht-truncate">
                              {meta.name}
                            </div>
                            <div className="cht-text-[10px] cht-text-gray-500">
                              {isSelected ? "Active" : "Excluded"}
                            </div>
                          </div>
                          <div
                            className={`cht-w-4 cht-h-4 cht-rounded-full cht-flex cht-items-center cht-justify-center ${
                              isSelected
                                ? "cht-bg-[#3e57da] cht-text-white"
                                : "cht-border cht-border-gray-300"
                            }`}
                          >
                            {isSelected && <Check size={10} />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Submit Broadcast Button */}
                <div className="cht-pt-4 cht-border-t cht-border-gray-100 cht-flex cht-items-center cht-justify-end cht-gap-3">
                  <Button
                    type="submit"
                    size="lg"
                    loading={sendBroadcastMutation.isPending}
                    disabled={!message.trim() || selectedPlatforms.length === 0}
                    leftIcon={<Send size={16} />}
                    className="cht-w-full sm:cht-w-auto"
                  >
                    {sendBroadcastMutation.isPending
                      ? "Dispatching to Social Feeds..."
                      : `Broadcast to ${selectedPlatforms.length} Platform(s)`}
                  </Button>
                </div>
              </form>
            </div>
          </div>

          {/* Interactive Live Preview Card */}
          <div className="lg:cht-col-span-5 cht-space-y-4 cht-sticky cht-top-6">
            <div className="cht-bg-white cht-rounded-2xl cht-border cht-border-gray-200/80 cht-shadow-sm cht-p-5">
              <div className="cht-flex cht-items-center cht-justify-between cht-mb-4">
                <div className="cht-flex cht-items-center cht-gap-2">
                  <Eye size={16} className="cht-text-[#3e57da]" />
                  <span className="cht-text-sm cht-font-bold cht-text-gray-800">
                    Interactive Feed Preview
                  </span>
                </div>
                {/* Platform Preview Selector */}
                <div className="cht-flex cht-items-center cht-gap-1 cht-bg-gray-100 cht-p-1 cht-rounded-lg">
                  {selectedPlatforms.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPreviewPlatform(p)}
                      className={`cht-p-1.5 cht-rounded-md cht-transition-all ${
                        previewPlatform === p
                          ? "cht-bg-white cht-text-[#3e57da] cht-shadow-sm"
                          : "cht-text-gray-500 hover:cht-text-gray-800"
                      }`}
                      title={PLATFORMS_META[p].name}
                    >
                      {PLATFORMS_META[p].iconSvg}
                    </button>
                  ))}
                </div>
              </div>

              {/* Feed Card Simulation */}
              <div className="cht-rounded-xl cht-border cht-border-gray-200 cht-bg-white cht-overflow-hidden cht-shadow-sm">
                {/* Simulated Header */}
                <div className="cht-p-3.5 cht-border-b cht-border-gray-100 cht-flex cht-items-center cht-gap-3">
                  <div className="cht-w-10 cht-h-10 cht-rounded-full cht-bg-gradient-to-tr cht-from-[#3e57da] cht-to-indigo-500 cht-flex cht-items-center cht-justify-center cht-text-white cht-font-black cht-text-xs cht-shadow-sm">
                    CT
                  </div>
                  <div className="cht-flex-1">
                    <div className="cht-text-xs cht-font-bold cht-text-gray-900 cht-flex cht-items-center cht-gap-1.5">
                      <span>Codehub Technologies</span>
                      <span className="cht-text-blue-500">✓</span>
                    </div>
                    <div className="cht-text-[11px] cht-text-gray-400 cht-flex cht-items-center cht-gap-1">
                      <span>Just now</span>
                      <span>•</span>
                      <span className="cht-capitalize">{previewPlatform}</span>
                    </div>
                  </div>
                  <div
                    className="cht-w-6 cht-h-6 cht-rounded-full cht-flex cht-items-center cht-justify-center cht-text-white"
                    style={{ backgroundColor: PLATFORMS_META[previewPlatform].brandColor }}
                  >
                    {PLATFORMS_META[previewPlatform].iconSvg}
                  </div>
                </div>

                {/* Simulated Content Body */}
                <div className="cht-p-4 cht-space-y-3">
                  {title && (
                    <div className="cht-font-bold cht-text-sm cht-text-gray-900">
                      {title}
                    </div>
                  )}

                  <div className="cht-text-xs cht-text-gray-700 cht-whitespace-pre-wrap cht-leading-relaxed">
                    {message || (
                      <span className="cht-text-gray-400 cht-italic">
                        Your post message will appear here in real-time as you type...
                      </span>
                    )}
                  </div>

                  {/* Simulated Attached Image */}
                  {imageUrl ? (
                    <div className="cht-rounded-xl cht-overflow-hidden cht-border cht-border-gray-200 cht-bg-gray-100">
                      <img
                        src={imageUrl}
                        alt="Simulated post attachment"
                        className="cht-w-full cht-h-48 cht-object-cover"
                        onError={(e) => {
                          (e.target as HTMLElement).style.display = "none";
                        }}
                      />
                    </div>
                  ) : null}

                  {/* Simulated Destination Link Card */}
                  {linkUrl ? (
                    <div className="cht-rounded-xl cht-border cht-border-gray-200 cht-bg-slate-50 cht-p-3 cht-flex cht-items-center cht-gap-3">
                      <div className="cht-w-8 cht-h-8 cht-rounded-lg cht-bg-white cht-border cht-border-gray-200 cht-flex cht-items-center cht-justify-center cht-text-indigo-600 cht-shrink-0">
                        <Globe size={16} />
                      </div>
                      <div className="cht-flex-1 cht-min-w-0">
                        <div className="cht-text-[11px] cht-font-semibold cht-text-indigo-600 cht-truncate">
                          {linkUrl}
                        </div>
                        <div className="cht-text-[10px] cht-text-gray-400 cht-truncate">
                          Codehub Technologies Official
                        </div>
                      </div>
                      <ExternalLink size={12} className="cht-text-gray-400" />
                    </div>
                  ) : null}
                </div>

                {/* Simulated Interaction Footer */}
                <div className="cht-px-4 cht-py-2.5 cht-bg-gray-50/70 cht-border-t cht-border-gray-100 cht-flex cht-items-center cht-justify-between cht-text-[11px] cht-text-gray-500">
                  <span>👍 ❤️ 💡 Broadcast Ready</span>
                  <span>Direct API Delivery</span>
                </div>
              </div>
            </div>

            {/* Platform Help Note */}
            <div className="cht-bg-indigo-50/50 cht-rounded-2xl cht-border cht-border-indigo-100 cht-p-4 cht-text-xs cht-text-indigo-900 cht-space-y-1">
              <div className="cht-font-bold cht-flex cht-items-center cht-gap-1.5">
                <Info size={14} className="cht-text-indigo-600" />
                <span>Asynchronous Background Dispatch</span>
              </div>
              <p className="cht-text-indigo-700 cht-leading-relaxed">
                Posts are dispatched simultaneously in a multi-threaded background pool. You can monitor
                live status, post IDs, and platform links in the Campaign History tab.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: CAMPAIGN HISTORY */}
      {activeTab === "history" && (
        <div className="cht-space-y-4">
          {/* Search & Actions Bar */}
          <div className="cht-bg-white cht-rounded-2xl cht-border cht-border-gray-200/80 cht-p-4 cht-flex cht-flex-col sm:cht-flex-row sm:cht-items-center sm:cht-justify-between cht-gap-3">
            <div className="cht-relative cht-flex-1">
              <Search
                size={16}
                className="cht-absolute cht-left-3.5 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400"
              />
              <input
                type="text"
                value={historySearch}
                onChange={(e) => setHistorySearch(e.target.value)}
                placeholder="Search past campaigns by keyword, title, or link..."
                className="cht-w-full cht-pl-10 cht-pr-3.5 cht-py-2 cht-text-sm cht-bg-gray-50 cht-border cht-border-gray-200 cht-rounded-xl focus:cht-bg-white focus:cht-border-[#3e57da] cht-outline-none cht-transition-all"
              />
            </div>
            <div className="cht-text-xs cht-text-gray-500">
              Showing {filteredBroadcasts.length} of {broadcasts.length} campaigns
            </div>
          </div>

          {/* Broadcasts List */}
          {broadcastsLoading ? (
            <div className="cht-bg-white cht-rounded-2xl cht-p-12 cht-text-center cht-border cht-border-gray-200">
              <RefreshCw size={24} className="cht-animate-spin cht-text-[#3e57da] cht-mx-auto cht-mb-2" />
              <div className="cht-text-sm cht-font-bold cht-text-gray-700">Loading broadcast history...</div>
            </div>
          ) : filteredBroadcasts.length === 0 ? (
            <div className="cht-bg-white cht-rounded-2xl cht-p-12 cht-text-center cht-border cht-border-gray-200">
              <div className="cht-w-12 cht-h-12 cht-rounded-2xl cht-bg-indigo-50 cht-text-indigo-600 cht-flex cht-items-center cht-justify-center cht-mx-auto cht-mb-3">
                <Share2 size={24} />
              </div>
              <h3 className="cht-text-base cht-font-bold cht-text-gray-800">No broadcasts found</h3>
              <p className="cht-text-xs cht-text-gray-500 cht-max-w-md cht-mx-auto cht-mt-1">
                {historySearch
                  ? "No broadcast campaign matches your search filter."
                  : "You haven't dispatched any social media broadcasts yet. Switch to the Composer tab to send your first post!"}
              </p>
            </div>
          ) : (
            <div className="cht-space-y-3">
              {filteredBroadcasts.map((broadcast) => {
                return (
                  <div
                    key={broadcast.id}
                    className="cht-bg-white cht-rounded-2xl cht-border cht-border-gray-200/80 cht-p-5 cht-shadow-sm hover:cht-border-gray-300 cht-transition-all"
                  >
                    <div className="cht-flex cht-flex-col lg:cht-flex-row lg:cht-items-start lg:cht-justify-between cht-gap-4">
                      {/* Left: Content Details */}
                      <div className="cht-flex-1 cht-space-y-2">
                        <div className="cht-flex cht-items-center cht-gap-2">
                          <span className="cht-text-xs cht-font-bold cht-px-2 cht-py-0.5 cht-rounded-md cht-bg-indigo-50 cht-text-[#3e57da]">
                            Broadcast #{broadcast.id}
                          </span>
                          <span className="cht-text-xs cht-text-gray-400">
                            {new Date(broadcast.created_at).toLocaleString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </div>

                        {broadcast.title && (
                          <h4 className="cht-text-sm cht-font-bold cht-text-gray-900">
                            {broadcast.title}
                          </h4>
                        )}

                        <p className="cht-text-xs cht-text-gray-600 cht-line-clamp-2 cht-leading-relaxed">
                          {broadcast.message}
                        </p>

                        {/* Link / Image Chips */}
                        <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-2 cht-pt-1">
                          {broadcast.link_url && (
                            <a
                              href={broadcast.link_url}
                              target="_blank"
                              rel="noreferrer"
                              className="cht-inline-flex cht-items-center cht-gap-1 cht-text-[11px] cht-text-indigo-600 hover:cht-underline"
                            >
                              <LinkIcon size={11} />
                              <span className="cht-truncate cht-max-w-xs">{broadcast.link_url}</span>
                            </a>
                          )}
                          {broadcast.image_url && (
                            <a
                              href={broadcast.image_url}
                              target="_blank"
                              rel="noreferrer"
                              className="cht-inline-flex cht-items-center cht-gap-1 cht-text-[11px] cht-text-purple-600 hover:cht-underline"
                            >
                              <ImageIcon size={11} />
                              <span>Attached Image</span>
                            </a>
                          )}
                        </div>
                      </div>

                      {/* Right: Platform Delivery Status Badges */}
                      <div className="cht-flex cht-flex-col sm:cht-flex-row lg:cht-flex-col cht-items-start lg:cht-items-end cht-gap-2.5">
                        <div className="cht-flex cht-flex-wrap cht-gap-1.5">
                          {broadcast.logs && broadcast.logs.length > 0 ? (
                            broadcast.logs.map((log) => {
                              const meta = PLATFORMS_META[log.platform as SocialPlatform];
                              return (
                                <div
                                  key={log.id}
                                  className="cht-flex cht-items-center cht-gap-1.5 cht-px-2.5 cht-py-1 cht-rounded-lg cht-bg-gray-50 cht-border cht-border-gray-200"
                                >
                                  <span
                                    className="cht-w-3.5 cht-h-3.5 cht-flex cht-items-center cht-justify-center cht-text-gray-700"
                                    style={{ color: meta?.brandColor }}
                                  >
                                    {meta?.iconSvg || log.platform}
                                  </span>
                                  <span className="cht-text-[11px] cht-font-bold cht-capitalize">
                                    {log.platform}
                                  </span>
                                  {renderStatusBadge(log.status)}
                                  {log.external_post_url && (
                                    <a
                                      href={log.external_post_url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="cht-text-gray-400 hover:cht-text-[#3e57da] cht-ml-0.5"
                                      title="Open published post"
                                    >
                                      <ExternalLink size={12} />
                                    </a>
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            <span className="cht-text-xs cht-text-gray-400">
                              Dispatched to: {broadcast.target_platforms.join(", ")}
                            </span>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="cht-flex cht-items-center cht-gap-2 cht-pt-1">
                          <button
                            type="button"
                            onClick={() => setInspectBroadcast(broadcast)}
                            className="cht-text-xs cht-font-semibold cht-text-[#3e57da] hover:cht-underline cht-flex cht-items-center cht-gap-1"
                          >
                            <Eye size={13} />
                            <span>Details</span>
                          </button>
                          <span className="cht-text-gray-300">•</span>
                          <button
                            type="button"
                            onClick={() => setDeletingBroadcastId(broadcast.id)}
                            className="cht-text-xs cht-font-semibold cht-text-rose-600 hover:cht-underline cht-flex cht-items-center cht-gap-1"
                          >
                            <Trash2 size={13} />
                            <span>Delete</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* TAB 3: EXECUTION AUDIT LOGS */}
      {activeTab === "logs" && (
        <div className="cht-space-y-4">
          {/* Filter Bar */}
          <div className="cht-bg-white cht-rounded-2xl cht-border cht-border-gray-200/80 cht-p-4 cht-flex cht-flex-wrap cht-items-center cht-justify-between cht-gap-3">
            <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-2">
              <span className="cht-text-xs cht-font-bold cht-text-gray-600 cht-flex cht-items-center cht-gap-1">
                <Filter size={13} />
                <span>Filters:</span>
              </span>

              {/* Platform filter */}
              <select
                value={logPlatformFilter}
                onChange={(e) => setLogPlatformFilter(e.target.value)}
                className="cht-text-xs cht-px-3 cht-py-1.5 cht-rounded-xl cht-bg-gray-50 cht-border cht-border-gray-200 cht-text-gray-700 focus:cht-border-[#3e57da] cht-outline-none"
              >
                <option value="all">All Platforms</option>
                <option value="facebook">Facebook</option>
                <option value="instagram">Instagram</option>
                <option value="twitter">X / Twitter</option>
                <option value="linkedin">LinkedIn</option>
                <option value="threads">Threads</option>
                <option value="tiktok">TikTok</option>
              </select>

              {/* Status filter */}
              <select
                value={logStatusFilter}
                onChange={(e) => setLogStatusFilter(e.target.value)}
                className="cht-text-xs cht-px-3 cht-py-1.5 cht-rounded-xl cht-bg-gray-50 cht-border cht-border-gray-200 cht-text-gray-700 focus:cht-border-[#3e57da] cht-outline-none"
              >
                <option value="all">All Statuses</option>
                <option value="published">Published</option>
                <option value="failed">Failed</option>
                <option value="pending">Pending</option>
                <option value="skipped">Skipped</option>
              </select>
            </div>

            <div className="cht-text-xs cht-text-gray-500">
              Total {logs.length} execution audit events
            </div>
          </div>

          {/* Logs Table */}
          {logsLoading ? (
            <div className="cht-bg-white cht-rounded-2xl cht-p-12 cht-text-center cht-border cht-border-gray-200">
              <RefreshCw size={24} className="cht-animate-spin cht-text-[#3e57da] cht-mx-auto cht-mb-2" />
              <div className="cht-text-sm cht-font-bold cht-text-gray-700">Loading audit logs...</div>
            </div>
          ) : logs.length === 0 ? (
            <div className="cht-bg-white cht-rounded-2xl cht-p-12 cht-text-center cht-border cht-border-gray-200">
              <FileText size={24} className="cht-text-gray-400 cht-mx-auto cht-mb-2" />
              <h3 className="cht-text-sm cht-font-bold cht-text-gray-700">No logs match the criteria</h3>
            </div>
          ) : (
            <div className="cht-bg-white cht-rounded-2xl cht-border cht-border-gray-200/80 cht-overflow-hidden cht-shadow-sm">
              <div className="cht-overflow-x-auto">
                <table className="cht-w-full cht-text-left cht-text-xs">
                  <thead className="cht-bg-gray-50/75 cht-border-b cht-border-gray-200 cht-text-gray-500 cht-font-semibold">
                    <tr>
                      <th className="cht-py-3 cht-px-4">Time</th>
                      <th className="cht-py-3 cht-px-4">Platform</th>
                      <th className="cht-py-3 cht-px-4">Broadcast ID</th>
                      <th className="cht-py-3 cht-px-4">Status</th>
                      <th className="cht-py-3 cht-px-4">Post Link / ID</th>
                      <th className="cht-py-3 cht-px-4">Diagnostic Trace</th>
                    </tr>
                  </thead>
                  <tbody className="cht-divide-y cht-divide-gray-100">
                    {logs.map((log) => {
                      const meta = PLATFORMS_META[log.platform as SocialPlatform];
                      return (
                        <tr key={log.id} className="hover:cht-bg-gray-50/50 cht-transition-colors">
                          <td className="cht-py-3.5 cht-px-4 cht-text-gray-500 cht-whitespace-nowrap">
                            {new Date(log.created_at).toLocaleString("en-US", {
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              second: "2-digit",
                            })}
                          </td>
                          <td className="cht-py-3.5 cht-px-4">
                            <span className="cht-inline-flex cht-items-center cht-gap-1.5 cht-font-bold cht-capitalize cht-text-gray-800">
                              <span style={{ color: meta?.brandColor }}>{meta?.iconSvg}</span>
                              {log.platform}
                            </span>
                          </td>
                          <td className="cht-py-3.5 cht-px-4 cht-text-gray-600">
                            #{log.broadcast}
                          </td>
                          <td className="cht-py-3.5 cht-px-4">{renderStatusBadge(log.status)}</td>
                          <td className="cht-py-3.5 cht-px-4">
                            {log.external_post_url ? (
                              <a
                                href={log.external_post_url}
                                target="_blank"
                                rel="noreferrer"
                                className="cht-text-[#3e57da] hover:cht-underline cht-inline-flex cht-items-center cht-gap-1 cht-font-medium"
                              >
                                <span>View Post</span>
                                <ExternalLink size={11} />
                              </a>
                            ) : log.external_post_id ? (
                              <span className="cht-font-mono cht-text-[11px] cht-text-gray-600">
                                {log.external_post_id}
                              </span>
                            ) : (
                              <span className="cht-text-gray-400">-</span>
                            )}
                          </td>
                          <td className="cht-py-3.5 cht-px-4 cht-max-w-md">
                            {log.error_message ? (
                              <span
                                className="cht-text-rose-600 cht-truncate cht-block"
                                title={log.error_message}
                              >
                                {log.error_message}
                              </span>
                            ) : log.status === "published" ? (
                              <span className="cht-text-emerald-600 cht-font-medium">Success</span>
                            ) : (
                              <span className="cht-text-gray-400">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 4: PLATFORM CREDENTIALS & SETTINGS */}
      {activeTab === "credentials" && isSuperAdmin && (
        <div className="cht-space-y-6">
          <div className="cht-bg-indigo-50/50 cht-rounded-2xl cht-border cht-border-indigo-100 cht-p-4 cht-flex cht-items-start cht-gap-3">
            <Shield size={20} className="cht-text-[#3e57da] cht-shrink-0 cht-mt-0.5" />
            <div className="cht-text-xs cht-text-indigo-900 cht-space-y-1">
              <div className="cht-font-bold">Hybrid Credential Architecture</div>
              <p className="cht-text-indigo-700 cht-leading-relaxed">
                The autoposter prioritizes database overrides configured below. If no database record
                exists for a platform, it automatically falls back to your server environment variables
                (<code className="cht-bg-white/80 cht-px-1 cht-py-0.5 cht-rounded cht-text-[11px]">.env</code> /{" "}
                <code className="cht-bg-white/80 cht-px-1 cht-py-0.5 cht-rounded cht-text-[11px]">settings.SOCIAL_AUTO_POST</code>).
              </p>
            </div>
          </div>

          <div className="cht-grid cht-grid-cols-1 md:cht-grid-cols-2 lg:cht-grid-cols-3 cht-gap-5">
            {ALL_PLATFORMS.map((platform) => {
              const meta = PLATFORMS_META[platform];
              const config = configs.find((c) => c.platform === platform);
              const isDbConfigured = Boolean(config);
              const isEnabled = config ? config.is_enabled : true;

              return (
                <div
                  key={platform}
                  className="cht-bg-white cht-rounded-2xl cht-border cht-border-gray-200/80 cht-p-5 cht-shadow-sm cht-space-y-4"
                >
                  <div className="cht-flex cht-items-center cht-justify-between">
                    <div className="cht-flex cht-items-center cht-gap-3">
                      <div
                        className="cht-w-9 cht-h-9 cht-rounded-xl cht-flex cht-items-center cht-justify-center cht-text-white cht-shadow-sm"
                        style={{ backgroundColor: meta.brandColor }}
                      >
                        {meta.iconSvg}
                      </div>
                      <div>
                        <h3 className="cht-text-sm cht-font-bold cht-text-gray-900">{meta.name}</h3>
                        <p className="cht-text-[11px] cht-text-gray-400">{meta.helpText}</p>
                      </div>
                    </div>

                    <span
                      className={`cht-text-xs cht-px-2.5 cht-py-0.5 cht-rounded-full cht-font-bold ${
                        isEnabled
                          ? "cht-bg-emerald-50 cht-text-emerald-700 cht-border cht-border-emerald-200"
                          : "cht-bg-gray-100 cht-text-gray-600 cht-border cht-border-gray-200"
                      }`}
                    >
                      {isEnabled ? "Enabled" : "Disabled"}
                    </span>
                  </div>

                  <div className="cht-bg-gray-50 cht-rounded-xl cht-p-3 cht-space-y-2 cht-text-xs">
                    <div className="cht-flex cht-items-center cht-justify-between">
                      <span className="cht-text-gray-500">Source:</span>
                      <span className="cht-font-semibold cht-text-gray-800">
                        {isDbConfigured ? "Database Override" : "Environment (.env Fallback)"}
                      </span>
                    </div>
                    {config?.page_or_account_id && (
                      <div className="cht-flex cht-items-center cht-justify-between">
                        <span className="cht-text-gray-500">Account / Page ID:</span>
                        <span className="cht-font-mono cht-text-[11px] cht-text-gray-700">
                          {config.page_or_account_id}
                        </span>
                      </div>
                    )}
                    <div className="cht-flex cht-items-center cht-justify-between">
                      <span className="cht-text-gray-500">Token Status:</span>
                      <span className="cht-font-semibold">
                        {config?.has_access_token || config?.has_bearer_token || config?.has_access_token_secret ? (
                          <span className="cht-text-emerald-600">Configured in DB ✓</span>
                        ) : (
                          <span className="cht-text-gray-500">Using .env or not set</span>
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="cht-flex cht-items-center cht-justify-end cht-gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openConfigModal(platform)}
                      leftIcon={<Sliders size={13} />}
                    >
                      Configure Credentials
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* MODAL 1: MEDIA LIBRARY SELECTOR */}
      <MediaPickerModal
        isOpen={isMediaPickerOpen}
        onClose={() => setIsMediaPickerOpen(false)}
        allowedTypes={["image"]}
        title="Select Image from Media Library"
        selectedUrl={imageUrl}
        onSelect={(asset: MediaAsset) => {
          setImageUrl(asset.url);
          setIsMediaPickerOpen(false);
          addToast({
            message: `Selected image "${asset.title}" from Media Library.`,
            bg: "success",
          });
        }}
      />

      {/* MODAL 2: INSPECT BROADCAST DETAILS */}
      <Modal
        isOpen={Boolean(inspectBroadcast)}
        onClose={() => setInspectBroadcast(null)}
        title={`Broadcast #${inspectBroadcast?.id || ""}`}
        size="lg"
      >
        {inspectBroadcast && (
          <div className="cht-space-y-4 cht-text-xs">
            <div>
              <div className="cht-font-bold cht-text-gray-700 cht-mb-1">Title:</div>
              <div className="cht-p-2.5 cht-bg-gray-50 cht-rounded-xl cht-font-medium">
                {inspectBroadcast.title || "No Title Provided"}
              </div>
            </div>

            <div>
              <div className="cht-font-bold cht-text-gray-700 cht-mb-1">Message Content:</div>
              <div className="cht-p-3 cht-bg-gray-50 cht-rounded-xl cht-whitespace-pre-wrap cht-leading-relaxed">
                {inspectBroadcast.message}
              </div>
            </div>

            {inspectBroadcast.link_url && (
              <div>
                <div className="cht-font-bold cht-text-gray-700 cht-mb-1">Destination URL:</div>
                <a
                  href={inspectBroadcast.link_url}
                  target="_blank"
                  rel="noreferrer"
                  className="cht-text-[#3e57da] hover:cht-underline cht-flex cht-items-center cht-gap-1"
                >
                  <span>{inspectBroadcast.link_url}</span>
                  <ExternalLink size={12} />
                </a>
              </div>
            )}

            {inspectBroadcast.image_url && (
              <div>
                <div className="cht-font-bold cht-text-gray-700 cht-mb-1">Attached Image:</div>
                <img
                  src={inspectBroadcast.image_url}
                  alt="Broadcast preview"
                  className="cht-w-full cht-max-h-64 cht-object-contain cht-rounded-xl cht-border cht-border-gray-200"
                />
              </div>
            )}

            {/* Platform Results Breakdown */}
            <div>
              <div className="cht-font-bold cht-text-gray-700 cht-mb-2">Platform Dispatches:</div>
              <div className="cht-space-y-2">
                {inspectBroadcast.logs?.map((log) => {
                  const meta = PLATFORMS_META[log.platform as SocialPlatform];
                  return (
                    <div
                      key={log.id}
                      className="cht-p-3 cht-rounded-xl cht-border cht-border-gray-200 cht-bg-gray-50 cht-space-y-1.5"
                    >
                      <div className="cht-flex cht-items-center cht-justify-between">
                        <span className="cht-font-bold cht-capitalize cht-flex cht-items-center cht-gap-1.5">
                          <span style={{ color: meta?.brandColor }}>{meta?.iconSvg}</span>
                          {log.platform}
                        </span>
                        {renderStatusBadge(log.status)}
                      </div>

                      {log.external_post_url && (
                        <div className="cht-flex cht-items-center cht-gap-1">
                          <span className="cht-text-gray-500">Live Post:</span>
                          <a
                            href={log.external_post_url}
                            target="_blank"
                            rel="noreferrer"
                            className="cht-text-[#3e57da] hover:cht-underline cht-font-medium"
                          >
                            {log.external_post_url}
                          </a>
                        </div>
                      )}

                      {log.error_message && (
                        <div className="cht-p-2 cht-rounded-lg cht-bg-rose-50 cht-text-rose-700 cht-text-[11px]">
                          <strong>Error:</strong> {log.error_message}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* MODAL 3: DELETE CONFIRMATION */}
      <Modal
        isOpen={Boolean(deletingBroadcastId)}
        onClose={() => setDeletingBroadcastId(null)}
        title="Delete Social Broadcast Record"
        size="sm"
      >
        <div className="cht-space-y-4 cht-text-xs">
          <div className="cht-p-3 cht-rounded-xl cht-bg-rose-50 cht-text-rose-800 cht-flex cht-items-start cht-gap-2">
            <AlertTriangle size={16} className="cht-text-rose-600 cht-shrink-0" />
            <p>
              Are you sure you want to delete Broadcast #{deletingBroadcastId}? This removes the audit
              log record from the database. Note that posts already published to social media networks
              will remain live on their respective platforms.
            </p>
          </div>
          <div className="cht-flex cht-items-center cht-justify-end cht-gap-2 cht-pt-2">
            <Button variant="ghost" size="sm" onClick={() => setDeletingBroadcastId(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              loading={deleteBroadcastMutation.isPending}
              onClick={handleDeleteConfirm}
            >
              Confirm Delete
            </Button>
          </div>
        </div>
      </Modal>

      {/* MODAL 4: EDIT PLATFORM CREDENTIALS (SUPERADMIN) */}
      <Modal
        isOpen={Boolean(editingConfig !== null || configForm.page_or_account_id !== undefined && activeTab === "credentials" && editingConfig !== undefined)}
        onClose={() => setEditingConfig(null)}
        title={`Configure ${PLATFORMS_META[configForm.platform as SocialPlatform]?.name || "Platform"}`}
        size="md"
      >
        <form onSubmit={handleSaveConfig} className="cht-space-y-3 cht-text-xs">
          <div className="cht-flex cht-items-center cht-justify-between cht-p-3 cht-bg-gray-50 cht-rounded-xl">
            <span className="cht-font-bold cht-text-gray-700">Enable Autoposting</span>
            <input
              type="checkbox"
              checked={configForm.is_enabled}
              onChange={(e) => setConfigForm({ ...configForm, is_enabled: e.target.checked })}
              className="cht-w-4 cht-h-4 cht-accent-[#3e57da]"
            />
          </div>

          <div>
            <label className="cht-block cht-font-bold cht-text-gray-700 cht-mb-1">
              {configForm.platform === "threads"
                ? "Threads User ID (Account ID)"
                : configForm.platform === "tiktok"
                ? "TikTok Creator / Account ID"
                : configForm.platform === "facebook"
                ? "Facebook Page ID"
                : configForm.platform === "instagram"
                ? "Instagram Business Account ID"
                : configForm.platform === "linkedin"
                ? "LinkedIn Author URN / Org ID"
                : "Page / Account / Org ID"}
            </label>
            <input
              type="text"
              value={configForm.page_or_account_id || ""}
              onChange={(e) =>
                setConfigForm({ ...configForm, page_or_account_id: e.target.value })
              }
              placeholder={
                configForm.platform === "threads"
                  ? "e.g. 17841400000000000"
                  : configForm.platform === "tiktok"
                  ? "e.g. open_id or creator_id"
                  : "e.g. 1029384756"
              }
              className="cht-w-full cht-px-3 cht-py-2 cht-border cht-border-gray-200 cht-rounded-xl cht-bg-gray-50 focus:cht-bg-white focus:cht-border-[#3e57da] cht-outline-none"
            />
          </div>

          <div>
            <label className="cht-block cht-font-bold cht-text-gray-700 cht-mb-1">
              Access Token <span className="cht-text-gray-400 cht-font-normal">(Leave blank to keep existing or use .env)</span>
            </label>
            <input
              type="password"
              value={configForm.access_token || ""}
              onChange={(e) => setConfigForm({ ...configForm, access_token: e.target.value })}
              placeholder="EAABw..."
              className="cht-w-full cht-px-3 cht-py-2 cht-border cht-border-gray-200 cht-rounded-xl cht-bg-gray-50 focus:cht-bg-white focus:cht-border-[#3e57da] cht-outline-none font-mono"
            />
          </div>

          {configForm.platform === "twitter" && (
            <>
              <div>
                <label className="cht-block cht-font-bold cht-text-gray-700 cht-mb-1">
                  API Key (Consumer Key)
                </label>
                <input
                  type="password"
                  value={configForm.api_key || ""}
                  onChange={(e) => setConfigForm({ ...configForm, api_key: e.target.value })}
                  placeholder="Leave blank to use .env"
                  className="cht-w-full cht-px-3 cht-py-2 cht-border cht-border-gray-200 cht-rounded-xl cht-bg-gray-50 focus:cht-bg-white focus:cht-border-[#3e57da] cht-outline-none font-mono"
                />
              </div>

              <div>
                <label className="cht-block cht-font-bold cht-text-gray-700 cht-mb-1">
                  API Secret (Consumer Secret)
                </label>
                <input
                  type="password"
                  value={configForm.api_secret || ""}
                  onChange={(e) => setConfigForm({ ...configForm, api_secret: e.target.value })}
                  placeholder="Leave blank to use .env"
                  className="cht-w-full cht-px-3 cht-py-2 cht-border cht-border-gray-200 cht-rounded-xl cht-bg-gray-50 focus:cht-bg-white focus:cht-border-[#3e57da] cht-outline-none font-mono"
                />
              </div>

              <div>
                <label className="cht-block cht-font-bold cht-text-gray-700 cht-mb-1">
                  Access Token Secret (OAuth 1.0a)
                </label>
                <input
                  type="password"
                  value={configForm.access_token_secret || ""}
                  onChange={(e) => setConfigForm({ ...configForm, access_token_secret: e.target.value })}
                  placeholder="Leave blank to use .env"
                  className="cht-w-full cht-px-3 cht-py-2 cht-border cht-border-gray-200 cht-rounded-xl cht-bg-gray-50 focus:cht-bg-white focus:cht-border-[#3e57da] cht-outline-none font-mono"
                />
              </div>

              <div>
                <label className="cht-block cht-font-bold cht-text-gray-700 cht-mb-1">
                  Bearer Token
                </label>
                <input
                  type="password"
                  value={configForm.bearer_token || ""}
                  onChange={(e) => setConfigForm({ ...configForm, bearer_token: e.target.value })}
                  placeholder="Leave blank to use .env"
                  className="cht-w-full cht-px-3 cht-py-2 cht-border cht-border-gray-200 cht-rounded-xl cht-bg-gray-50 focus:cht-bg-white focus:cht-border-[#3e57da] cht-outline-none font-mono"
                />
              </div>
            </>
          )}

          <div className="cht-flex cht-items-center cht-justify-end cht-gap-2 cht-pt-3">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setEditingConfig(null)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              loading={createConfigMutation.isPending || updateConfigMutation.isPending}
            >
              Save Configuration
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default SocialBroadcastView;
